import eventBus from "@sd-utility/eventbus";
import { Button } from "@tvg/fuse/button";
import * as Icons from "@tvg/fuse/icon/all";
import { Modal as ModalFuse } from "@tvg/fuse/modal";
import { TextBlock } from "@tvg/fuse/text-block";
import useRender from "client/hooks/useRender";
import getEventHandler from "client/utils/eventHandler";
import getIconName from "client/utils/getIconName";
import React, { useCallback, useEffect, useMemo } from "react";
import { AmpModalConfig } from "types/amplience";
import { getCTAEvents } from "../../hooks/useCTAEvents";
import styles from "./Modal.module.css";

const backgroundConfig: { [key: string]: string } = {
  "Brand Light": "brandLight",
  "Brand Mid": "brandMid",
  Grey: "grey",
  Black: "black",
};

export interface ModalProps {
  config: AmpModalConfig;
}

export const Modal: React.FC<ModalProps> = ({ config: { modalKey, header, content } }) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const render = useRender();

  useEffect(() => {
    const listener = eventBus.subscribe<string>("modal/open", (eventData) => {
      if (eventData === modalKey) setModalOpen(true);
    });
    return listener.unsubscribe;
  }, [modalKey]);

  const handleClose = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleCTAClick = useMemo(() => {
    if (!content.cta?.publishEvent) return;

    return getEventHandler(content.cta?.publishEvent);
  }, [content.cta?.publishEvent]);

  const headlineHTML = { __html: !content.headline?.headlineText ? "" : render(content.headline.headlineText) };

  const mainContentHTML = {
    __html: !content.mainContent?.mainContentText ? "" : render(content.mainContent.mainContentText),
  };

  const Icon = header.icon && Icons[getIconName(header.icon) as keyof typeof Icons];

  return (
    <ModalFuse
      titleTag="h2"
      className={modalKey.replace("modal/", "modal-")}
      headerIcon={Icon && <Icon />}
      dataTestId="content-modal"
      disablePadding
      title={header.title}
      srLabelCloseButton="Close modal"
      zIndex="99" // Setting this value initially to work with Search and Menu which have z-index of 100
      open={modalOpen}
      onClose={handleClose}
    >
      {content.headline && content.headline.headlineText && (
        <TextBlock
          className={styles.modal__text}
          background={content.headline.background ? backgroundConfig[content.headline.background] : undefined}
        >
          <div dangerouslySetInnerHTML={headlineHTML} />
        </TextBlock>
      )}

      {content.mainContent && content.mainContent.mainContentText && (
        <TextBlock className={styles.modal__text}>
          <div dangerouslySetInnerHTML={mainContentHTML} />
        </TextBlock>
      )}

      {content.cta && (content.cta.url || content.cta.publishEvent) && (
        <div className={styles.modal__buttonContainer}>
          <Button
            data-cta-event={getCTAEvents(content.cta)}
            href={content.cta.url}
            iconRight
            icon={<Icons.IconChevronRight />}
            onClick={handleCTAClick}
          >
            {content.cta.text}
          </Button>
        </div>
      )}
    </ModalFuse>
  );
};
